import React, { useEffect, useRef, useState } from 'react';
import styles from './GenericSelect.module.scss';
import { Identifiable } from '../../../global';

interface GenericSelectProps<T> {
  items: T[];
  value?: T | null;
  onChange: (value: T | null) => void;
  placeholder?: string;
  renderItem: (item: T) => string;
  allowNull?: boolean;
  nullLabel?: string;
}

export function GenericSelect<T extends Identifiable>({
  items,
  value,
  onChange,
  placeholder = 'Select...',
  renderItem,
  allowNull = false,
  nullLabel = 'None',
}: GenericSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(0);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLDivElement>(null);

  const fullList: (T | null)[] = allowNull ? [null, ...items] : items;

  // --- Outside click + ESC ---
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    if (open) {
      toggleRef.current?.focus();
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  // --- Sync highlight when opening ---
  useEffect(() => {
    if (!open) return;

    const idx = fullList.findIndex(
      (i) => i !== null && i.id === value?.id
    );

    setHighlightedIndex(idx >= 0 ? idx : 0);
  }, [open, value, items, allowNull]);

  // --- Keyboard navigation (ONLY HERE) ---
  const handleKeyNavigation = (e: React.KeyboardEvent) => {
  if (!open) {
    if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      setOpen(true);
      return;
    }
    return;
  }

    if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (e.key === 'ArrowDown') {
      setHighlightedIndex((prev) =>
        Math.min(prev + 1, fullList.length - 1)
      );
    }

    if (e.key === 'ArrowUp') {
      setHighlightedIndex((prev) => Math.max(prev - 1, 0));
    }

    if (e.key === 'Enter') {
      const selected = fullList[highlightedIndex];
      onChange(selected);
      setOpen(false);
    }
  };

  const isSelected = (item: T | null) => {
    return item?.id === value?.id ? styles.selected : '';
  };

  return (
    <div ref={wrapperRef} className={styles.select_wrapper}>
      <div className={`${styles.dropdown} ${open ? styles.open : ''}`}>
        {/* Toggle */}
        <div
          ref={toggleRef}
          className={styles.dropdown__toggle}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          tabIndex={0}
          onKeyDown={handleKeyNavigation}
          onClick={() => setOpen((v) => !v)}
        >
          <span className={`${styles.dropdown__label} w-75`}>
            {value ? renderItem(value) : placeholder}
          </span>
          <div className={styles.arrow}></div>
        </div>

        {/* Options */}
        {open && (
          <div className={styles.dropdown__optionslist} role="listbox">
            {allowNull && (
              <>
                <div
                  className={`${styles.dropdown__option} ${isSelected(null)} ${
                    highlightedIndex === 0 ? styles.highlighted : ''
                  }`}
                  onClick={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                >
                  {nullLabel}
                </div>
                <div className={styles.dropdown__spacer}></div>
              </>
            )}

            {items.map((item, idx) => {
              const realIndex = allowNull ? idx + 1 : idx;

              return (
                <div
                  key={item.id}
                  ref={(el) => {
                    if (realIndex === highlightedIndex && el) {
                      el.scrollIntoView({ block: 'nearest' });
                    }
                  }}
                  className={`${styles.dropdown__option} ${isSelected(item)} ${
                    realIndex === highlightedIndex ? styles.highlighted : ''
                  }`}
                  onClick={() => {
                    onChange(item);
                    setOpen(false);
                  }}
                >
                  {renderItem(item)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default GenericSelect;
